import { SearchOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Col, Input, message, Pagination, Popconfirm, Row, Space, Table, Tooltip } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as ArchiveRestore } from '../../Images/ArchiveRestore.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { fetchArchivedQuotationList, unArchiveQuotation } from '../../slices/quotation';
import dayjs from 'dayjs';

export default function ArchiveQuotationList() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(10);
  const [orderBy, setOrderBy] = useState('');
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      fetchArchivedQuotationList({
        page,
        pagesize,
        ...(search && { search: search }),
        ...(orderBy && { ordering: orderBy }),
      }),
    );
  }, [dispatch, page, pagesize, search, orderBy]);


  const { data = [], totalRecord, status } = useSelector(state => state.quotation);

  const dataSource = data.map((item, index) => ({
    ...item,
    key: item.id || index,
    SrNo: (page - 1) * pagesize + index + 1, // Calculate Sr. No. based on page and limit
  }));

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const handleRestore = (id) => {
    if (!id) {
      message.error('Missing ID for deletion!');
      return;
    }

    dispatch(unArchiveQuotation(id))
      .unwrap()
      .then(() => {
        message.success('Quotation Unarchived successfully!');
        dispatch(
          fetchArchivedQuotationList({ page, pagesize, ...(search && { search: search }) }),
        );
      })
      .catch((err) => {
        console.error('Delete Error:', err); // Debugging the error
        message.error(`Error: ${err.message || 'Failed to delete the quotation.'}`);
      });
  };
  const debounceTimeout = useRef(null);
  const handleSearch = (e) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setSearch(e.target.value);
      setPage(1);
    }, 100); // 300ms delay
  };
  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "id";

    if (sortField === "SrNo" && sortOrder === "ascend") {
      sortingEnumKey = "SrNo";
    }
    if (sortField === "SrNo" && sortOrder === "descend") {
      sortingEnumKey = "-SrNo";
    }
    if (sortField === "quotation_date" && sortOrder === "ascend") {
      sortingEnumKey = "quotation_date";
    }
    if (sortField === "quotation_date" && sortOrder === "descend") {
      sortingEnumKey = "-quotation_date";
    }
    if (sortField === "quotation_no" && sortOrder === "ascend") {
      sortingEnumKey = "quotation_no";
    }
    if (sortField === "quotation_no" && sortOrder === "descend") {
      sortingEnumKey = "-quotation_no";
    }
    if (sortField === "customer" && sortOrder === "ascend") {
      sortingEnumKey = "customer";
    }
    if (sortField === "customer" && sortOrder === "descend") {
      sortingEnumKey = "-customer";
    }
    if (sortField === "work_order_no" && sortOrder === "ascend") {
      sortingEnumKey = "work_order_no";
    }
    if (sortField === "work_order_no" && sortOrder === "descend") {
      sortingEnumKey = "-work_order_no";
    }
    if (sortField === "converted_date" && sortOrder === "ascend") {
      sortingEnumKey = "converted_date";
    }
    if (sortField === "converted_date" && sortOrder === "descend") {
      sortingEnumKey = "-converted_date";
    }
    if (sortField === "status" && sortOrder === "ascend") {
      sortingEnumKey = "status";
    }
    if (sortField === "status" && sortOrder === "descend") {
      sortingEnumKey = "-status";
    }
    return sortingEnumKey;
  };
  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    if (sorter?.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
    setPageSize(pagination.pageSize);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };
  const columns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo',
      key: 'SrNo',
      width: 100,
      sorter: true
    },
    {
      title: 'Quotation Date',
      dataIndex: 'quotation_date',
      key: 'quotation_date',
      sorter: true,
      render: (quotation_date) => {
        return (
          <span>{quotation_date
            ? dayjs(quotation_date).format("DD-MM-YYYY")
            : "N/A"}</span>
        );
      },
    },
    {
      title: 'Quotation No.',
      dataIndex: 'quotation_no',
      key: 'quotation_no',
      sorter: true,
      render: (quotation_no) => {
        return (
          <span>{quotation_no ? quotation_no : 'N/A'}</span>
        );
      },
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      sorter: true,
      render: (customer) => {
        return (
          <span>{customer?.customer_name || 'N/A'}</span>
        );
      },

    },
    {
      title: 'WorkOrder No.',
      dataIndex: 'workorder_no',
      key: 'workorder_no',
      sorter: true,
      render: (workorder_no) => {
        return (
          <span>{workorder_no ? workorder_no : 'Not Converted to Workorder'}</span>
        );
      },
    },
    {
      title: 'Converted Date',
      dataIndex: 'converted_date',
      key: 'converted_date',
      sorter: true,
      render: (converted_date) => {
        return (
          <span>{converted_date
            ? dayjs(converted_date).format("DD-MM-YYYY")
            : "N/A"}</span>
        );
      },
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      sorter: true,
      render: (status) => {
        return (
          <span>{status ? status : 'N/A'}</span>
        );
      },
    },
    {
      title: 'Archive At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (record) => {
        return record ? (
          <span>{dayjs(record).format('DD-MM-YYYY HH:mm')}</span>
        ) : (
          <span>N/A</span>
        );
      }
    },
    {
      title: 'Archive By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      render: (record) => {
        return (
          <span>{record ? `${record.first_name} ${record.last_name}` : 'N/A'} </span>
        )
      }

    },
    {
      title: 'Action',
      dataIndex: 'action',
      render: (_, record) => (
        <Space>
          <Tooltip placement="top" title="Restore">
            <Popconfirm
              title="Are you sure you want to Unarchive this record?"
              onConfirm={() => handleRestore(record.id)} // Trigger delete on confirmation
              onCancel={() => message.info('Action cancelled')} // Optional cancel action
              okText="Yes"
              cancelText="No"
            >
              <ArchiveRestore className="ArchiverestorImg" />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];
  const handlePageChange = (newPage) => {
    setPage(newPage);
    setOrderBy('')
  };
  const resetFilter = () => {
    setSearch('');
    setOrderBy('');
    setPage(1);
  };
  const handlePageSizeChange = (current, size) => {
    setPage(1); // Reset to first page if needed
    setPageSize(size); // You must have a setter or state update
  };
  return (
    <>
      <div className='sticyaction'>
        <Row className="pagenamerow" justify="space-between" align="middle">
          <Col>
            <h2>Archive Quotations</h2>
            <div className="bredcrumbwrp">
              <Link to="/quotation-list" className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  { title: <Link to="/dashboard">Home</Link> },
                  { title: <Link to="/quotation-list">Quotations</Link> },
                  { title: 'Archive Quotations' }
                ]}
              />
            </div>
          </Col>
          <Col>
            <Space size="middle">
              <Input placeholder="Search"
                  className='searchbox'
                value={search}
                onChange={handleSearch}
                prefix={<SearchOutlined />} style={{ width: 200 }} />
              <Link>
                <Tooltip title="Filter">
                  <Button type="primary" disabled={!search && !orderBy} className='iconlink' onClick={resetFilter}>
                    <FilterIcon />
                  </Button>
                </Tooltip>
              </Link>
            </Space>
          </Col>
        </Row>
      </div>
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, overflow: 'auto' }}>

      <Table
        //rowSelection={rowSelection}
        dataSource={dataSource}
        columns={columns}
        // pagination={{
        //   defaultCurrent: page,
        //   defaultPageSize: pagesize,
        //   total: totalRecord,
        //   showTotal: (total) => `Total ${total} items`,
        //   showSizeChanger: true,
        //   onChange: handlePageChange,
        //   locale: {
        //     items_per_page: 'Items / Page', // Text for the size changer
        //   },
        // }}
        pagination={false}
        scroll={{
          x: 880, // Horizontal Scroll
          y: 400,  // Vertical Scroll with Fixed Header
        }}
        className="mt-5"
        onChange={handleTableChange}      
      />
       </div>
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1000,
            background: '#fff',
            padding: 10,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Pagination
            current={page}
            pageSize={pagesize}
            total={totalRecord}
            showSizeChanger
            onShowSizeChange={handlePageSizeChange}
            showTotal={total => `Total ${total} items`}
            onChange={handlePageChange}
            locale={{ items_per_page: 'Items / Page' }}
          />
        </div>
      </div>
    </>
  );
}
